/**
 * holdings - auto-detected external holdings tracker (Crypto page)
 *
 * Turns the Watchlist capital-move detector (services/watchlist.ts) into a
 * tracked "account": for every enabled watch group flagged
 * `excludeFromSpending` (seeded "Crypto" group is ON by default), this
 * computes how much capital moved out/back over ALL transactions, then adds
 * user adjustments (market gains/losses, value syncs) to produce a tracked
 * value. Surfaces: /crypto page + a small auto-appearing section on the
 * Accounts page.
 *
 * DESIGN RULES (see CRYPTO_HOLDINGS_PLAN.md):
 * - Derived + presentation-only: transactions, balances and analytics are
 *   NEVER touched. No real Account row is created (it would double-count -
 *   the money already left checking as an expense).
 * - Detection semantics are IDENTICAL to the Analytics capital-move matcher:
 *   expense/income only (no transfers), `[adjustment]` descriptions skipped,
 *   same matchGroup() word-boundary rules - both surfaces always agree.
 * - Adjustments SYNC across devices (2026-07-20): they live in the Dexie
 *   `holdingsAdjustments` table with the standard syncStatus/soft-delete
 *   pattern and ride the normal push/pull pipeline. The original v1
 *   localStorage store is migrated in-place once per device (records land
 *   as `pending` so they push to the server on the next sync).
 */

import { db, Transaction, HoldingsAdjustment, HoldingsAdjustmentKind } from './db';
import { generateId } from '../utils/id';
import { syncManager } from './syncManager';
import { WatchGroup, matchGroup } from './watchlist';

// Re-exports so existing call sites/tests keep one import surface
export type { HoldingsAdjustment } from './db';
export type AdjustmentKind = HoldingsAdjustmentKind;

// ============================================================
// Types
// ============================================================

export interface HoldingsMatch {
  id: string;
  date: Date;
  description: string;
  amount: number;
  type: 'expense' | 'income';
}

export interface HoldingsSnapshot {
  group: WatchGroup;
  /** Capital sent out (matched expenses), all time. */
  movedOut: number;
  /** Capital that came back (matched income), all time. */
  movedBack: number;
  /** movedOut - movedBack. */
  netMoved: number;
  /** Sum of signed adjustments for this group. */
  adjustmentTotal: number;
  /** netMoved + adjustmentTotal - the headline number. */
  trackedValue: number;
  matchCount: number;
  lastActivity?: Date;
  /** Most recent detected transactions, newest first. */
  recentMatches: HoldingsMatch[];
  adjustments: HoldingsAdjustment[];
}

// ============================================================
// Adjustments store (Dexie `holdingsAdjustments`, synced)
// ============================================================

/** Legacy v1 localStorage key - migrated into Dexie once per device. */
const LEGACY_STORAGE_KEY = 'ninjacash-holdings';
const LEGACY_BACKUP_KEY = 'ninjacash-holdings-v1-backup';
/** Window event fired on any adjustments change (cross-hook refresh). */
export const HOLDINGS_CHANGED_EVENT = 'ninjacash-holdings-changed';

const MAX_ADJUSTMENTS = 500;
const MAX_NOTE_LENGTH = 120;

interface LegacyStoredAdjustment {
  id: string;
  groupId: string;
  date: string; // ISO
  amount: number;
  kind: HoldingsAdjustmentKind;
  note?: string;
}

interface LegacyStoredHoldings {
  version: number;
  adjustments?: LegacyStoredAdjustment[];
  migrated?: boolean;
}

export function emitHoldingsChanged(): void {
  try {
    window.dispatchEvent(new Event(HOLDINGS_CHANGED_EVENT));
  } catch {
    // non-browser environment (tests)
  }
}

/**
 * One-time migration of the v1 localStorage adjustments into Dexie.
 * Records land with syncStatus 'pending' so they push to the server on the
 * next sync - this is what heals devices that made adjustments before the
 * store became synced. Idempotent (bulkPut by id + a migrated marker); the
 * original payload is kept under a backup key.
 */
let migrationPromise: Promise<void> | null = null;

async function migrateLegacyAdjustments(): Promise<void> {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(LEGACY_STORAGE_KEY);
  } catch {
    return; // localStorage unavailable - nothing to migrate
  }
  if (!raw) return;

  let parsed: LegacyStoredHoldings | null = null;
  try {
    parsed = JSON.parse(raw) as LegacyStoredHoldings;
  } catch {
    // Corrupted legacy payload - drop it (matches old loadAdjustments behavior)
    try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { /* ignore */ }
    return;
  }
  if (!parsed || parsed.migrated) return;

  const legacy = Array.isArray(parsed.adjustments)
    ? parsed.adjustments.filter(
        (a) =>
          a &&
          typeof a.id === 'string' &&
          typeof a.groupId === 'string' &&
          typeof a.amount === 'number' &&
          Number.isFinite(a.amount)
      )
    : [];

  if (legacy.length > 0) {
    const now = new Date();
    const records: HoldingsAdjustment[] = legacy.map((a) => {
      const when = a.date ? new Date(a.date) : now;
      const date = Number.isNaN(when.getTime()) ? now : when;
      return {
        id: a.id,
        groupId: a.groupId,
        date,
        amount: a.amount,
        kind: a.kind || 'correction',
        note: typeof a.note === 'string' ? a.note.slice(0, MAX_NOTE_LENGTH) : undefined,
        createdAt: date,
        updatedAt: now,
        syncStatus: 'pending', // Must push - these never reached the server
      };
    });
    await db.holdingsAdjustments.bulkPut(records);
    console.log(`[HOLDINGS] Migrated ${records.length} legacy adjustment(s) to synced store`);
    syncManager.triggerPush();
  }

  try {
    localStorage.setItem(LEGACY_BACKUP_KEY, raw);
    localStorage.setItem(
      LEGACY_STORAGE_KEY,
      JSON.stringify({ version: 2, migrated: true } satisfies LegacyStoredHoldings)
    );
  } catch {
    // Marker write failed - migration stays idempotent via bulkPut anyway
  }
}

function ensureMigrated(): Promise<void> {
  if (!migrationPromise) {
    migrationPromise = migrateLegacyAdjustments().catch((err) => {
      // Leave the legacy payload untouched so a later load can retry
      migrationPromise = null;
      console.error('[HOLDINGS] Legacy adjustment migration failed:', err);
    });
  }
  return migrationPromise;
}

/** Test hook: reset the migration singleton (fresh DB per test). */
export function _resetMigrationForTests(): void {
  migrationPromise = null;
}

/** Active (non-deleted) adjustments, newest first. */
export async function loadAdjustments(): Promise<HoldingsAdjustment[]> {
  await ensureMigrated();
  const all = await db.holdingsAdjustments
    .filter((a) => !a.deletedAt)
    .toArray();
  return all.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export async function addAdjustment(input: {
  groupId: string;
  amount: number;
  kind: AdjustmentKind;
  note?: string;
}): Promise<HoldingsAdjustment | null> {
  if (!Number.isFinite(input.amount) || input.amount === 0) return null;

  await ensureMigrated();

  const activeCount = await db.holdingsAdjustments.filter((a) => !a.deletedAt).count();
  if (activeCount >= MAX_ADJUSTMENTS) {
    console.warn(`[HOLDINGS] Adjustment cap reached (${MAX_ADJUSTMENTS}) - not adding`);
    return null;
  }

  const now = new Date();
  const adjustment: HoldingsAdjustment = {
    id: generateId(),
    groupId: input.groupId,
    date: now,
    amount: input.amount,
    kind: input.kind,
    note: input.note?.trim().slice(0, MAX_NOTE_LENGTH) || undefined,
    createdAt: now,
    updatedAt: now,
    syncStatus: 'pending',
  };

  await db.holdingsAdjustments.put(adjustment);
  emitHoldingsChanged();
  syncManager.triggerPush();
  return adjustment;
}

/** Soft delete (deletedAt + pending) so the deletion syncs to other devices. */
export async function removeAdjustment(id: string): Promise<void> {
  await ensureMigrated();
  const updated = await db.holdingsAdjustments.update(id, {
    deletedAt: new Date(),
    updatedAt: new Date(),
    syncStatus: 'pending',
  });
  if (updated > 0) {
    emitHoldingsChanged();
    syncManager.triggerPush();
  }
}

// ============================================================
// Compute
// ============================================================

function isAdjustmentDescription(desc: string | undefined): boolean {
  if (!desc) return false;
  const lower = desc.toLowerCase();
  return lower.includes('[adjustment]') || lower.startsWith('adjustment');
}

/** Groups that produce a tracker: enabled + capital-move + has terms. */
export function getTrackedGroups(groups: WatchGroup[]): WatchGroup[] {
  return groups.filter(
    (g) => g.enabled && g.excludeFromSpending && g.terms.length > 0
  );
}

const RECENT_MATCH_LIMIT = 8;

/**
 * Compute one snapshot per tracked group from ALL transactions.
 * Pure function - no storage access (adjustments passed in).
 */
export function computeHoldings(
  transactions: Transaction[],
  groups: WatchGroup[],
  adjustments: HoldingsAdjustment[]
): HoldingsSnapshot[] {
  const tracked = getTrackedGroups(groups);
  if (tracked.length === 0) return [];

  // Defensive: soft-deleted adjustments never count (loadAdjustments already
  // filters them, but callers may pass raw table contents)
  const active = adjustments.filter((a) => !a.deletedAt);

  return tracked.map((group) => {
    let movedOut = 0;
    let movedBack = 0;
    let matchCount = 0;
    let lastActivity: Date | undefined;
    const matches: HoldingsMatch[] = [];

    for (const txn of transactions) {
      if (txn.type !== 'expense' && txn.type !== 'income') continue;
      if (isAdjustmentDescription(txn.description)) continue;
      if (!matchGroup(txn.description || '', group)) continue;

      matchCount += 1;
      const when = new Date(txn.date);
      if (!lastActivity || when > lastActivity) lastActivity = when;

      if (txn.type === 'expense') movedOut += txn.amount;
      else movedBack += txn.amount;

      matches.push({
        id: txn.id || '',
        date: when,
        description: txn.description || '',
        amount: txn.amount,
        type: txn.type,
      });
    }

    matches.sort((a, b) => b.date.getTime() - a.date.getTime());

    const groupAdjustments = active.filter((a) => a.groupId === group.id);
    const adjustmentTotal = groupAdjustments.reduce((sum, a) => sum + a.amount, 0);
    const netMoved = movedOut - movedBack;

    return {
      group,
      movedOut,
      movedBack,
      netMoved,
      adjustmentTotal,
      trackedValue: netMoved + adjustmentTotal,
      matchCount,
      lastActivity,
      recentMatches: matches.slice(0, RECENT_MATCH_LIMIT),
      adjustments: groupAdjustments,
    };
  });
}

/**
 * "Set current value" - the one-tap drift fix. Records a single value_sync
 * adjustment equal to (target - currentTrackedValue) so the tracked value
 * lands exactly on what the user sees in their wallet.
 * Returns null when the delta is zero (already in sync).
 */
export async function syncToValue(
  groupId: string,
  currentTrackedValue: number,
  targetValue: number,
  note?: string
): Promise<HoldingsAdjustment | null> {
  if (!Number.isFinite(targetValue)) return null;
  const delta = Math.round((targetValue - currentTrackedValue) * 100) / 100;
  if (delta === 0) return null;
  return addAdjustment({ groupId, amount: delta, kind: 'value_sync', note });
}
